/**
 * ONE place that reads a number a member TYPED, in the member's own notation.
 *
 * FOUNDER, R2: "1,5" typed on the German site used to be read as 15 — every input stripped "," as a
 * thousands mark and took "." as the decimal. That is a silent data error on a financial plan, so the rule
 * here is: read by the member's number locale (the one `numberFormat.ts` writes in), and when a figure
 * could honestly mean two different amounts, REFUSE and name both readings instead of guessing.
 *
 * HOW A FIGURE IS READ
 *  - Spaces (plain, no-break, narrow no-break) and apostrophes (' ’) are always thousands marks.
 *  - Both "," and "." present: the LAST one is the decimal mark, the other groups ("1.234,5", "1,234.5").
 *  - One mark, repeated: grouping ("1.234.567", "12,34,567").
 *  - One mark, once, NOT followed by exactly three digits: the decimal mark ("1,5" -> 1.5 in any locale;
 *    it cannot be a thousands mark).
 *  - One mark, once, followed by exactly three digits ("1,500", "1.500") — the only two-way case. What
 *    the field holds decides (`kind`):
 *      plain   the locale decides: its thousands mark groups, its decimal mark is a decimal, any other
 *              mark is refused;
 *      amount  (money) the locale's thousands mark groups; its decimal mark is refused — "250,000" typed
 *              as an amount on the German site is almost always a foreign thousands mark;
 *      rate    (percentages, terms, stored decimals) the locale's decimal mark is a decimal; its thousands
 *              mark is refused — "3.125" typed as a rate on the German site is not 3 125 %;
 *      strict  (a small stored integer, where either reading is plausible) always refused.
 *  - Groups must be well formed (1–3 leading digits then threes, or the Indian 2-2-3 pattern).
 *
 * WORDS. An amount may carry a currency (a symbol, an ISO code, or a symbol-word like "kr") and a scale
 * word of the member's OWN language ("1.5 million", "1,2 Mio.", "5 Mrd", "250 Tsd.", "2 lakh",
 * "1,5 millones", "10 mil") — numberWords.ts. "billion" is 1e9 in English and 1e12 in German, and "mil"
 * is a thousand in Spanish and refused in English, so only the member's language's words count. Any other
 * letters are refused, never stripped (an earlier version stripped "million" as if it were a currency code
 * and saved 1.5). A word English uses for a different scale ("2 billion" on the German or French site:
 * Billion is 1e12 there, 1e9 in English) is refused with both readings.
 *
 * The server reads chat figures with a twin (backend/app/services/locale_number.py) pinned by the same
 * case table (backend/tests/fixtures/locale_number_cases.json). The page sends it the MARKS it resolved
 * here (`numberMarks`), so the two never disagree about a regional locale.
 */
import { CURRENCY_CODES, CURRENCY_WORDS, SCALE_WORDS } from './numberWords'

export type NumberParse =
  | { ok: true; value: number }
  | { ok: false; reason: 'empty' | 'invalid' }
  /** Two honest readings; the caller asks the member which one they meant. */
  | { ok: false; reason: 'ambiguous'; readings: [number, number] }

export type NumberKind = 'plain' | 'amount' | 'rate' | 'strict'

export type Marks = { decimal: '.' | ','; group: string }

export type ParseOptions = {
  /** The member's number locale (`useNumberLocale()`); there is no global default. */
  locale: string
  /** Money: currency symbols/codes and the language's scale words are read; kind defaults to 'amount'. */
  money?: boolean
  /** Percent: a "%" (with any spacing) is dropped; kind defaults to 'rate'. The value stays in points. */
  percent?: boolean
  /** What the field holds, for the one two-way case (see the header). Default: plain. */
  kind?: NumberKind
  /** The marks to read with, when already resolved (the server is sent them); default: the locale's. */
  marks?: Marks
}

/** Any space is written as U+00A0 and any apostrophe as ’, so marks compare across ICU builds. */
export function canonicalGroup(g: string): string {
  if (/^[\s\u00a0\u202f\u2009]$/.test(g)) return '\u00a0'
  if (/^['\u2019\u02bc]$/.test(g)) return '\u2019'
  return g
}

const _marks = new Map<string, Marks>()
/** The locale's decimal mark and (canonical) thousands mark, as Intl writes them. */
export function numberMarks(locale: string): Marks {
  let m = _marks.get(locale)
  if (!m) {
    let parts: Intl.NumberFormatPart[]
    try {
      parts = new Intl.NumberFormat(locale).formatToParts(1234567.5)
    } catch {
      parts = new Intl.NumberFormat('en-US').formatToParts(1234567.5)
    }
    const dec = parts.find((p) => p.type === 'decimal')?.value === ',' ? ',' : '.'
    const group = canonicalGroup(parts.find((p) => p.type === 'group')?.value ?? (dec === ',' ? '.' : ','))
    m = { decimal: dec, group }
    _marks.set(locale, m)
  }
  return m
}

function languageOf(locale: string): string {
  return locale.toLowerCase().split(/[-_]/)[0] || 'en'
}

const SPACE_GROUP = /[\s\u00a0\u202f\u2009'\u2019\u02bc]/g
const NUMERIC_CORE = /^[\d.,\s\u00a0\u202f\u2009'\u2019\u02bc]+$/
const WESTERN_GROUPS = /^\d{1,3}(\|\d{3})*$/
const INDIAN_GROUPS = /^\d{1,2}(\|\d{2})*\|\d{3}$/
const CODES = new Set(CURRENCY_CODES.map((c) => c.toLowerCase()))
const CURRENCY_WORD_SET = new Set(CURRENCY_WORDS.map((w) => w.toLowerCase()))
/** Latin and Cyrillic letters: a word of these ends at a non-letter ("m" must not eat "mxn"). */
const WORDY = /[a-z\u00c0-\u024f\u0400-\u04ff]/i

function groupsOk(intPart: string): boolean {
  // `intPart` has every thousands mark already turned into "|".
  if (!intPart.includes('|')) return /^\d*$/.test(intPart)
  return WESTERN_GROUPS.test(intPart) || INDIAN_GROUPS.test(intPart)
}

function toNumber(intPart: string, frac: string): number {
  return Number(`${intPart.replace(/\|/g, '') || '0'}${frac ? `.${frac}` : ''}`)
}

/** Nothing, or a currency on its own ("EUR", "kr", "zł", "円"), with an optional trailing ".". */
function isCurrencyOrNothing(t: string): boolean {
  const w = t.trim().toLowerCase().replace(/\.$/, '')
  return !w || CODES.has(w) || CURRENCY_WORD_SET.has(w)
}

/**
 * The text after the last digit: an optional scale word of the member's language, then an optional
 * currency. The multiplier, or null for anything else.
 */
// Spanish and Portuguese write thousands as "m"/"m€" (miles, mil) as often as millions; "M", "MM",
// "millones" and "milhões" are unambiguous. A bare lowercase "m" there is asked about.
const LOWERCASE_M_IS_TWO_WAY = new Set(['es', 'pt'])

function readSuffix(suffix: string, language: string): { mult: number; alt?: number } | null {
  const original = suffix.trim()
  const x = original.toLowerCase()
  if (!x) return { mult: 1 }
  const words: Record<string, number> = { ...SCALE_WORDS['*'], ...(SCALE_WORDS[language] ?? {}) }
  for (const w of Object.keys(words).sort((a, b) => b.length - a.length)) {
    if (!x.startsWith(w)) continue
    const next = x.charAt(w.length)
    if (next && WORDY.test(w.slice(-1)) && WORDY.test(next)) continue
    if (!isCurrencyOrNothing(x.slice(w.length).replace(/^\./, ''))) continue
    if (w === 'm' && original.startsWith('m') && LOWERCASE_M_IS_TWO_WAY.has(language)) return { mult: words[w], alt: 1e3 }
    const english = language === 'en' ? undefined : SCALE_WORDS.en?.[w]
    return english !== undefined && english !== words[w] ? { mult: words[w], alt: english } : { mult: words[w] }
  }
  return isCurrencyOrNothing(x) ? { mult: 1 } : null
}

/** Read `text` as a number in the member's notation. Never throws. */
export function parseLocaleNumber(text: string | null | undefined, opts: ParseOptions): NumberParse {
  // Bidi marks Intl puts around Arabic figures, and Arabic-Indic digits and marks a keyboard may type.
  let s = String(text ?? '')
    .replace(/[\u200e\u200f\u061c\u202a-\u202e\u2066-\u2069]/g, '')
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/\u066b/g, '.').replace(/\u066c/g, ',')
    .trim()
  if (!s) return { ok: false, reason: 'empty' }
  const marks = opts.marks ?? numberMarks(opts.locale)
  const kind: NumberKind = opts.kind ?? (opts.money ? 'amount' : opts.percent ? 'rate' : 'plain')
  const invalid: NumberParse = { ok: false, reason: 'invalid' }

  if (opts.percent) s = s.replace(/%/g, '').trim()
  // A currency symbol BETWEEN digits ("2€500", Portuguese-style "2$500") is not a thousands mark: refuse.
  if (opts.money && /\d\s*(?:\p{Sc}|(?:us|au|a|ca|c|hk|nt|s|nz|r|mx)\$|cn¥)\s*\d/iu.test(s)) return invalid
  // "US$", "R$", "HK$", "CA$", "MX$", "CN¥" first (their letters belong to the symbol, and the plan writes
  // them), then every currency symbol.
  if (opts.money) s = s.replace(/(?:(?:us|au|a|ca|c|hk|nt|s|nz|r|mx)\$|cn¥)/giu, ' ').replace(/\p{Sc}/gu, ' ').trim()

  // [prefix][number][suffix], split around the first and last digit.
  const first = s.search(/\d/)
  if (first < 0) return invalid
  let last = s.length - 1
  while (!/\d/.test(s[last])) last--
  let prefix = s.slice(0, first).trim()
  const suffix = s.slice(last + 1)
  let core = s.slice(first, last + 1)
  let sign = 1
  if (/[-\u2212]$/.test(prefix)) { sign = -1; prefix = prefix.slice(0, -1).trim() }
  let mult = 1
  let altMult: number | undefined
  if (opts.money) {
    if (!isCurrencyOrNothing(prefix)) return invalid
    const m = readSuffix(suffix, languageOf(opts.locale))
    if (m === null) return invalid
    mult = m.mult
    altMult = m.alt
  } else if (prefix || suffix.trim()) {
    return invalid
  }
  if (!NUMERIC_CORE.test(core)) return invalid

  const spaced = SPACE_GROUP.test(core)
  SPACE_GROUP.lastIndex = 0
  core = core.replace(SPACE_GROUP, '|')
  const lastDot = core.lastIndexOf('.'), lastComma = core.lastIndexOf(',')
  const done = (v: number): NumberParse => {
    if (!Number.isFinite(v)) return invalid
    // "2 billion" on the German site (2e12 there, 2e9 in English), "2 m" on the Spanish one — ask.
    if (altMult !== undefined) return { ok: false, reason: 'ambiguous', readings: [sign * v * mult, sign * v * altMult] }
    return { ok: true, value: sign * v * mult }
  }

  // No "," or "." at all: an integer, possibly spaced ("1 234", "1'234").
  if (lastDot < 0 && lastComma < 0) return groupsOk(core) ? done(toNumber(core, '')) : invalid

  // Both marks: the last one is the decimal, the other is grouping only.
  if (lastDot >= 0 && lastComma >= 0) {
    const dec = lastDot > lastComma ? '.' : ','
    const grp = dec === '.' ? ',' : '.'
    const at = core.lastIndexOf(dec)
    const intPart = core.slice(0, at), frac = core.slice(at + 1)
    if (intPart.includes(dec) || frac.includes(grp) || !/^\d+$/.test(frac)) return invalid
    const ip = intPart.split(grp).join('|')
    return groupsOk(ip) ? done(toNumber(ip, frac)) : invalid
  }

  const mark = lastDot >= 0 ? '.' : ','
  if (core.split(mark).length - 1 > 1) {
    // Repeated: only grouping reads ("1.234.567"); a spaced integer part then cannot also be grouped by it.
    const ip = core.split(mark).join('|')
    return !spaced && groupsOk(ip) ? done(toNumber(ip, '')) : invalid
  }
  const at = core.indexOf(mark)
  const intPart = core.slice(0, at), frac = core.slice(at + 1)
  if (!/^\d+$/.test(frac) || !groupsOk(intPart)) return invalid
  const asDecimal = toNumber(intPart, frac)
  // Already grouped by spaces/apostrophes ("1 234,5", "1’234.5"), or not three digits after: a decimal.
  if (spaced || frac.length !== 3) return done(asDecimal)
  // The two-way case: one mark, three digits after.
  const asGrouping = toNumber(`${intPart}|${frac}`, '')
  if (!groupsOk(`${intPart}|${frac}`) || /^0/.test(intPart)) return done(asDecimal)
  const ambiguous: NumberParse = { ok: false, reason: 'ambiguous', readings: [sign * asGrouping * mult, sign * asDecimal * mult] }
  const isGroup = mark === marks.group, isDecimal = mark === marks.decimal
  if (kind === 'plain') return isGroup ? done(asGrouping) : isDecimal ? done(asDecimal) : ambiguous
  if (kind === 'amount') return isGroup ? done(asGrouping) : ambiguous
  if (kind === 'rate') return isDecimal ? done(asDecimal) : ambiguous
  return ambiguous
}

/** The number, or null for anything that is not one clear number (empty, invalid or ambiguous). */
export function readLocaleNumber(text: string | null | undefined, opts: ParseOptions): number | null {
  const r = parseLocaleNumber(text, opts)
  return r.ok ? r.value : null
}

/**
 * A stored number as the text an edit field opens on, in the member's notation but WITHOUT grouping, so
 * it reads back exactly: 1234.5 -> "1234.5" (en) / "1234,5" (de). The digits are String(n)'s, never rounded.
 */
export function editableNumber(n: number | null | undefined, locale: string): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return ''
  const s = String(n)
  if (/e/i.test(s)) return s
  return numberMarks(locale).decimal === ',' ? s.replace('.', ',') : s
}

/**
 * The two readings of an ambiguous figure, with no words, so it needs no translation:
 * "1.500" on the French site -> "1 500 / 1,5 ?". Shown next to the field so the member retypes it.
 */
export function ambiguousReadings(readings: readonly [number, number], locale: string): string {
  let f: Intl.NumberFormat
  try {
    f = new Intl.NumberFormat(locale, { maximumFractionDigits: 6 })
  } catch {
    f = new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 })
  }
  return `${f.format(readings[0])} / ${f.format(readings[1])} ?`
}
